import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';

export interface ITableState {
  pageIndex: number;
  pageSize: number;
  sort: {
    active: string;
    direction: SortDirection;
  };
}

@Injectable({
  providedIn: 'root',
})
export class TableStateService {
  private readonly storage: Storage | null = typeof window === 'undefined' ? null : window.localStorage;
  private readonly storagePrefix: string = 'table-state';

  public buildKey(parentKey: string, tableKey: string): string {
    return `${parentKey}:${tableKey}`;
  }

  public clearState(key: string): void {
    if (!this.storage) {
      return;
    }

    this.storage.removeItem(this.buildStorageKey(key));
  }

  public getState(key: string): ITableState | null {
    if (!this.storage) {
      return null;
    }

    const rawState: string | null = this.storage.getItem(this.buildStorageKey(key));

    if (!rawState) {
      return null;
    }

    const parsedState: unknown = this.safeParse(rawState);

    if (!this.isTableState(parsedState)) {
      return null;
    }

    return parsedState;
  }

  public setState(key: string, state: ITableState): void {
    if (!this.storage) {
      return;
    }

    const payload: string = JSON.stringify(state);
    this.storage.setItem(this.buildStorageKey(key), payload);
  }

  private buildStorageKey(key: string): string {
    return `${this.storagePrefix}:${key}`;
  }

  private isTableState(state: unknown): state is ITableState {
    if (!state || typeof state !== 'object') {
      return false;
    }

    const tableState = state as ITableState;

    if (!tableState.sort || typeof tableState.sort !== 'object') {
      return false;
    }

    return (
      typeof tableState.pageIndex === 'number' &&
      typeof tableState.pageSize === 'number' &&
      typeof tableState.sort.active === 'string' &&
      typeof tableState.sort.direction === 'string'
    );
  }

  private safeParse(rawState: string): unknown {
    try {
      return JSON.parse(rawState) as unknown;
    } catch {
      return null;
    }
  }
}
